<template>
  <BillingFeatureGate feature="booking_from_offer"
                      variant="page"
                      :title="$t('billing.features.booking_from_offer.pageTitle')"
                      :description="$t('billing.features.booking_from_offer.tooltip')">
    <div>
      <Toolbar class="p-mb-4 p-mt-4 p-p-2">
        <template #start>
          <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('estimateBookingTitle') }}</div>
        </template>

        <template #end>
          <div class="p-d-flex p-flex-wrap p-ai-center">
            <div class="p-p-1">
              <Button @click="saveSettings"
                      :disabled="!$store.getters.isAdminOrHigher || locationIsLoading || fetchInFlight"
                      :loading="locationIsLoading || fetchInFlight"
                      class="p-button-raised"
                      icon="ti-save"
                      :label="$t('Save')"/>
            </div>
          </div>
        </template>
      </Toolbar>

      <div class="p-grid">
        <div class="p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-2">
          <fieldset class="p-fieldset p-pb-4">
            <legend class="p-fieldset-legend p-p-2">{{ $t('General settings') }}</legend>

            <div class="p-d-flex p-ai-center p-mt-3">
              <div class="p-d-flex p-ai-center">
                <InputSwitch id="use-booking-checkbox" v-model="useBooking"/>
              </div>
              <label for="use-booking-checkbox" class="switch-label pointer">
                {{ $t('enableEstimateBookingLabel') }}
              </label>
              <i v-tooltip="$t('enableEstimateBookingHelp')" class="ti-help-alt cursor-help"/>
            </div>

            <div class="p-mt-6">
              <div class="p-float-label">
                <InputNumber id="booking-min-days-ahead"
                             class="settings-input"
                             :inputClass="{ 'p-invalid' : submitted && bookingMinDaysAhead === null }"
                             :locale="computedNumberInputLocale"
                             v-model="bookingMinDaysAhead"
                             :min="0"
                             :max="30"
                             :minFractionDigits="0"
                             :maxFractionDigits="0"
                             showButtons
                             autocomplete="new-password"/>
                <label for="booking-min-days-ahead">{{ $t('bookingMinDaysAheadLabel') }}<span class="warning-color">*</span></label>
              </div>
              <small class="p-invalid" v-if="submitted && bookingMinDaysAhead === null">{{ $t('Required field') }}</small>
              <small class="p-d-flex p-ai-center p-mt-1 p-text-secondary">
                <i class="ti-info-alt p-mr-1"/>
                {{ $t('bookingMinDaysAheadHelp') }}
              </small>
            </div>

            <div class="p-mt-6">
              <div class="p-float-label">
                <InputNumber id="booking-max-days-ahead"
                             class="settings-input"
                             :inputClass="{ 'p-invalid' : submitted && bookingMaxDaysAhead === null }"
                             :locale="computedNumberInputLocale"
                             v-model="bookingMaxDaysAhead"
                             :min="1"
                             :max="90"
                             :minFractionDigits="0"
                             :maxFractionDigits="0"
                             showButtons
                             autocomplete="new-password"/>
                <label for="booking-max-days-ahead">
                  {{ $t('bookingMaxDaysAheadLabel') }}<span class="warning-color">*</span></label>
              </div>
              <small class="p-invalid" v-if="submitted && bookingMaxDaysAhead === null">{{ $t('Required field') }}</small>
              <small class="p-d-flex p-ai-center p-mt-1 p-text-secondary">
                <i class="ti-info-alt p-mr-1"/>
                {{ $t('bookingMaxDaysAheadHelp') }}
              </small>
            </div>

            <div class="p-mt-6">
              <div class="p-float-label">
                <InputNumber id="booking-step-minutes"
                             class="settings-input"
                             :inputClass="{ 'p-invalid' : submitted && !bookingStepMinutes }"
                             :locale="computedNumberInputLocale"
                             v-model="bookingStepMinutes"
                             :min="10"
                             :max="60"
                             :minFractionDigits="0"
                             :maxFractionDigits="0"
                             showButtons
                             autocomplete="new-password"/>
                <label for="booking-step-minutes">
                  {{ $t('bookingStepMinutesLabel') }} ({{ $t('minutes') }})<span class="warning-color">*</span></label>
              </div>
              <small class="p-invalid" v-if="submitted && !bookingStepMinutes">{{ $t('Required field') }}</small>
            </div>
          </fieldset>
        </div>

        <div class="p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-2">
          <fieldset class="p-fieldset p-pb-4">
            <legend class="p-fieldset-legend p-p-2">{{ $t('closedDatesTitle') }}</legend>

            <div class="closed-top p-mt-2">
              <Calendar v-model="closedDateToAdd"
                        class="closed-cal"
                        :minDate="new Date()"
                        :selectOtherMonths="true"
                        :disabled="closedDatesBusy || !canEditClosedDates"
                        :dateFormat="settings.dateFormat"
                        showIcon
                        :manualInput="false"/>

              <Button class="p-button-text"
                      icon="ti-plus"
                      :label="$t('addDateLabel')"
                      :disabled="closedDatesBusy || !canEditClosedDates"
                      @click="addClosedDate"/>
            </div>

            <small v-if="!useBooking" class="p-text-secondary">
              {{ $t('estimateBookingDisabledMsg') }}
            </small>
            <small v-else-if="useBookingDirty" class="p-text-secondary">
              {{ $t('saveSettingsToManageClosedDatesMsg') }}
            </small>

            <div v-if="closedDatesLoading" class="closed-state">
              <Spinner :isSmall="true"/>
            </div>

            <template v-else>
              <NoticePlaceholder v-if="!closedDates?.length"
                                 icon="ti-calendar"
                                 :title="$t('noClosedDatesTitle')"
                                 :text="$t('noClosedDatesText')"/>

              <div class="closed-wrap p-mt-2" v-if="closedDates?.length">
            <span v-for="it in closedDates"
                  :key="it.id ?? it.date"
                  class="closed-pill theme-border">
              <span class="closed-pill-date">{{ formatClosedDate(it.date) }}</span>

              <Button class="p-button-text p-button-danger p-button-sm closed-pill-x"
                      v-tooltip.top="$t('Delete')"
                      icon="ti-trash"
                      @click="removeClosedDate(it)"/>
            </span>
              </div>
            </template>
          </fieldset>
        </div>

        <div class="p-col-12 p-sm-12 p-md-12 p-lg-12">
          <fieldset class="p-fieldset">
            <legend class="p-fieldset-legend p-p-2">{{ $t('estimateBookingHoursTitle') }}</legend>
            <HoursEditor ref="bh"
                         v-model="bookingHours"
                         :submitted="submitted"/>
          </fieldset>
        </div>
      </div>
    </div>
  </BillingFeatureGate>
</template>

<script>
import httpClient from "@/services/http.services";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";
import HoursEditor from "@/pages/locations/components/HoursEditor.vue";
import NoticePlaceholder from "@/components/NoticePlaceholder";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureGate from '@/components/billing/BillingFeatureGate.vue'

export default {
  emits: ['update-items'],
  components: { HoursEditor, NoticePlaceholder, BillingFeatureGate },
  mixins: [showErrorsMixins, formatMixins, billingFeaturesMixins],
  props: {
    locationData: { type: Object, default: null },
    locationIsLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      // priceRequestEnabled: false,
      // closed dates
      closedDatesLoading: false,
      closedDates: [],
      closedDateToAdd: null,

      // booking settings
      bookingMinDaysAhead: null,
      bookingMaxDaysAhead: null,
      bookingStepMinutes: null,
      useBooking: false,
      originalUseBooking: null,

      bookingHours: null,

      fetchInFlight: false,
      submitted: false,
      settings
    }
  },
  mounted() {
    this.initializeFeaturePage();
  },
  watch: {
    billingEntitlementsReady() {
      this.initializeFeaturePage();
    },
    locationData: {
      deep: true,
      handler() {
        if (!this.canUseBookingFromOffer) return;
        this.applySettings();
      }
    }
  },
  methods: {
    initializeFeaturePage() {
      if (!this.billingEntitlementsReady || !this.canUseBookingFromOffer) return;

      this.applySettings();
      this.loadClosedDates();
    },

    toBool(v) {
      return v === 1 || v === '1' || v === true;
    },

    applySettings() {
      const s = this.locationData || {};

      const ub = this.toBool(s.use_booking);
      this.useBooking = ub;
      this.originalUseBooking = ub

      this.bookingMinDaysAhead = s.booking_min_days_ahead != null
          ? Number(s.booking_min_days_ahead)
          : 1

      this.bookingMaxDaysAhead = s.booking_max_days_ahead != null
          ? Number(s.booking_max_days_ahead)
          : 30

      this.bookingStepMinutes = s.booking_step_minutes != null
          ? Number(s.booking_step_minutes)
          : 30

      // this.priceRequestEnabled = this.toBool(s.is_price_request_enabled)

      this.bookingHours = (s.booking_hours && typeof s.booking_hours === 'object') ? s.booking_hours : null;
    },

    formatClosedDate(ymd) {
      if (!ymd) return '';
      const [y, m, d] = String(ymd).split('-').map(Number);
      const dt = new Date(y, (m || 1) - 1, d || 1);

      const locale = this.$store.state.branchData?.locale || settings.defaultLocale;
      return new Intl.DateTimeFormat(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }).format(dt);
    },
    formatDateYmd(d) {
      if (!d) return null;
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },

    async addClosedDate() {
      if (!this.canUseBookingFromOffer) return;
      if (!this.locationData?.id || !this.closedDateToAdd) return;

      const date = this.formatDateYmd(this.closedDateToAdd);
      if (!date) return;

      this.closedDatesLoading = true;
      try {
        const { data, status } = await httpClient.post('location/closed-dates-create', {
          location_id: this.locationData.id,
          dates: [date],
        });

        if (status === 200 && data?.success) {
          await this.loadClosedDates();
          this.closedDateToAdd = null;
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife });
        } else {
          this.showUnknownError();
        }
      } catch (e) {
        this.showError(e);
      } finally {
        this.closedDatesLoading = false;
      }
    },

    async removeClosedDate(it) {
      if (!this.canUseBookingFromOffer) return;
      if (!this.locationData?.id) return;
      const date = it?.date;
      if (!date) return;

      this.closedDatesLoading = true;
      try {
        const { data, status } = await httpClient.post('location/closed-dates-delete', {
          location_id: this.locationData.id,
          date,
        });

        if (status === 200 && data?.success) {
          this.closedDates = this.closedDates.filter(x => x.date !== date);
          this.$toast.add({ severity: 'success', detail: this.$t('Data deleted'), life: settings.toastLife });
        } else {
          this.showUnknownError();
        }
      } catch (e) {
        this.showError(e);
      } finally {
        this.closedDatesLoading = false;
      }
    },

    async loadClosedDates() {
      if (!this.canUseBookingFromOffer) return;
      if (!this.locationData?.id) return;

      this.closedDatesLoading = true;
      try {
        const { data, status } = await httpClient.get('location/closed-dates', {
          params: { location_id: this.locationData.id }
        });

        if (status === 200 && data?.success) {
          this.closedDates = data.items || [];
        } else {
          this.closedDates = [];
        }
      } catch (e) {
        this.showError(e);
        this.closedDates = [];
      } finally {
        this.closedDatesLoading = false;
      }
    },

    async saveSettings() {
      if (!this.canUseBookingFromOffer) return false;

      this.submitted = true;

      const locationId = this.locationData?.id;

      if (!locationId || this.fetchInFlight) return false;
      if (this.bookingMinDaysAhead === null || this.bookingMaxDaysAhead === null || this.bookingStepMinutes === null) {
        return false
      }

      if (this.bookingMinDaysAhead > this.bookingMaxDaysAhead) {
        this.$toast.add({
          severity: 'error',
          detail: this.$t('bookingMinMaxDaysError'),
          life: settings.toastLife
        })
        return false
      }

      if (this.$refs.bh && typeof this.$refs.bh.validate === 'function') {
        if (!this.$refs.bh.validate()) return false;
      }


      const bookingHoursPayload = (this.$refs.bh && typeof this.$refs.bh.getPayload === 'function')
          ? this.$refs.bh.getPayload()
          : this.bookingHours;

      this.fetchInFlight = true;

      const payload = {
        location_id: locationId,
        use_booking: this.useBooking ? 1 : 0,
        booking_min_days_ahead: this.bookingMinDaysAhead,
        booking_max_days_ahead: this.bookingMaxDaysAhead,
        booking_step_minutes: this.bookingStepMinutes,
        // is_price_request_enabled: this.priceRequestEnabled ? 1 : 0,
        booking_hours: bookingHoursPayload,
      };

      try {
        const { data, status } = await httpClient.post('location/settings-update', payload);
        if (status === 200 && data?.success) {
          this.$emit('update-items', locationId);
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife });
          this.originalUseBooking = this.useBooking;
        } else {
          this.showUnknownError();
        }
      } catch (err) {
        this.showError(err);
      } finally {
        this.fetchInFlight = false;
        this.submitted = false
      }
    },
  },
  computed: {
    useBookingDirty() {
      return this.originalUseBooking !== null && this.useBooking !== this.originalUseBooking;
    },
    canEditClosedDates() {
      return this.useBooking && !this.useBookingDirty;
    },
    closedDatesBusy() {
      return this.closedDatesLoading || this.fetchInFlight || this.locationIsLoading;
    }
  }
}
</script>

<style scoped lang="scss">
.settings-input {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber) {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
}

.settings-input :deep(.p-inputnumber-input),
.settings-input :deep(input.p-inputtext) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber-button-group) {
  flex: 0 0 auto;
}

.p-col-12,
.p-sm-12,
.p-md-6,
.p-lg-6,
.p-fieldset {
  min-width: 0;
}

.closed-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0 0;
  flex-wrap: wrap;
}

.closed-cal { width: 180px; }

.closed-state {
  display: flex;
  justify-content: center;
  padding: 18px 0;
}


.closed-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-top: 10px;
}

.closed-pill {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px 2px 10px;
}

.closed-pill-date {
  display: inline-block;
  width: 85px;
  text-align: left;
  font-weight: 500;
  white-space: nowrap;
}

.closed-pill-x {
  margin-left: 0;
}
</style>
